import styled from "styled-components";

interface ChipProps {
  variant: 'code' | 'time' | 'audience' | 'modality' | 'deadline' | 'files' | 'status';
}

interface ChipDotProps {
  color: string;
}

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0.625rem 1.25rem 0.375rem 1.25rem;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 0.5rem 1rem 0.25rem 1rem;
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  @media (max-width: 480px) {
    padding: 0.5rem 0.75rem 0.25rem 0.75rem;
  }
`;

export const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.125rem;

  @media (max-width: 768px) {
    margin-bottom: 0.5rem;
  }
`;

export const ContentSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    margin-bottom: 0.5rem;
  }

  @media (max-width: 480px) {
    gap: 0.75rem;
  }
`;

export const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    justify-content: flex-start;
    width: 100%;
  }
`;

export const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    justify-content: flex-end;
    width: 100%;
  }

  @media (max-width: 480px) {
    justify-content: center;
  }
`;

export const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  @media (max-width: 768px) {
  }
`;

export const MainTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.interfaceText400};
  margin: 0;
`;



export const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 37px;
  height: 37px;
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.colors.interfaceBorder100};
  border-radius: 8px;
  background: var(--action-square-bg);
  color: ${({ theme }) => theme.colors.interfaceText400};
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
  }
`;

export const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.8125rem;
  height: 1.8125rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.interfaceBackground};
  color: ${({ theme }) => theme.colors.interfaceText400};
`;

export const Divider = styled.div`
  width: 1px;
  height: 2rem;
  background: ${({ theme }) => theme.colors.interfaceBorder100};
  margin: 0 0.75rem;
`;

export const ChipContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  flex-wrap: wrap;
  width: 100%;

  @media (max-width: 768px) {
    gap: 0.375rem;
    align-items: flex-start;
  }

  @media (max-width: 480px) {
    gap: 0.25rem;
    align-items: flex-start;
  }
`;

export const Chip = styled.div<ChipProps>`
  display: flex;
  align-items: center;
  gap: 0.125rem;
  border-radius: 1rem;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;

  @media (max-width: 480px) {
    font-size: 12px;
  }
  
  ${({ variant }) => {
    switch (variant) {
      case 'code':
        return `
          background: ${(props: any) => props.theme.colors.white};
          border: 1px solid ${(props: any) => props.theme.colors.c1e3a8a};
          color: ${(props: any) => props.theme.colors.c1e3a8a};
          padding: 0.2rem 0.55rem;
        `;
      case 'time':
        return `
          background: ${(props: any) => props.theme.colors.interfaceGray300};
          color: ${(props: any) => props.theme.colors.pureWhite};
          padding: 0.2rem 0.55rem;
        `;
      case 'audience':
        return `
          background: ${(props: any) => props.theme.colors.cfce7f3};
          color: ${(props: any) => props.theme.colors.cbe185d};
          padding: 0.2rem 0.55rem;
        `;
      case 'modality':
        return `
          background: ${(props: any) => props.theme.colors.cdbeafe};
          color: ${(props: any) => props.theme.colors.c1e40af};
          padding: 0.2rem 0.55rem;
        `;
      case 'deadline':
        return `
          background: ${(props: any) => props.theme.colors.cf3f4f6};
          color: ${(props: any) => props.theme.colors.c374151};
          padding: 0.2rem 0.55rem;
          white-space: normal;
          flex-shrink: 1;
          min-width: 0;
          max-width: 100%;
          align-items: flex-start;
          
          @media (max-width: 768px) {
            padding: 0.2rem 0.5rem;
            max-width: 100%;
          }
          
          @media (max-width: 480px) {
            padding: 0.15rem 0.4rem;
            max-width: 100%;
          }
        `;
      case 'files':
        return `
          background: ${(props: any) => props.theme.colors.cf3e8ff};
          color: ${(props: any) => props.theme.colors.c7c3aed};
          padding: 0.2rem 0.55rem;
        `;
      case 'status':
        return `
          background: ${(props: any) => props.theme.colors.cf9fafb};
          color: ${(props: any) => props.theme.colors.c6b7280};
          padding: 0.2rem 0.55rem;
        `;
      default:
        return `
          background: ${(props: any) => props.theme.colors.cf3f4f6};
          color: ${(props: any) => props.theme.colors.c374151};
          padding: 0.25rem 0.65rem;
        `;
    }
  }}
`;

export const ChipIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 0.875rem;
  height: 0.875rem;
`;

export const ChipText = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  text-transform: capitalize;
`;

export const ChipTextDeadline = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  text-transform: capitalize;
  word-break: break-word;
  hyphens: auto;

  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 1.1;
  }
`;

export const ChipDot = styled.div<ChipDotProps>`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: ${({ color }) => color};
`;

export const SectionDivider = styled.div`
  width: 100%;
  height: 1px;
  background: ${(props: any) => props.theme.colors.ce0e0e0};
  margin: 1.5rem 0 0 0;
`; 